import { BanknotesIcon, PencilSquareIcon, TrashIcon, UsersIcon, WalletIcon } from "@heroicons/react/24/outline";
import { Card, MetricCard, RowAction, Table, Tag } from "../ui";
import { formatDateTime } from "../scholarships/review/model";
import { KIND_LABELS, formatRwf, nameOf, stateOf, stepLabel } from "./model";

export function ListStateTag({ state }) {
  const { label, color } = stateOf(state);
  return <Tag color={color}>{label}</Tag>;
}

// The list's head count and what it asks for
export function ListTotals({ students, totalTuition, totalAllowance }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <MetricCard title="Students" value={students.toLocaleString("en-US")} icon={<UsersIcon className="h-4 w-4" />} />
      <MetricCard title="Tuition" value={formatRwf(totalTuition)} icon={<BanknotesIcon className="h-4 w-4" />} />
      <MetricCard title="Allowances" value={formatRwf(totalAllowance)} icon={<WalletIcon className="h-4 w-4" />} />
    </div>
  );
}

// The lists as a table; staff and BRD also see which institution sent each
export const listColumns = (years, withInstitution) => [
  ...(withInstitution ? [{ key: "institution", label: "Institution", accessor: (l) => l.institutionName, filter: "Any institution", className: "font-semibold text-slate-900" }] : []),
  { key: "title", label: "List", accessor: (l) => l.title, className: withInstitution ? undefined : "font-semibold text-slate-900" },
  { key: "year", label: "Academic year", accessor: (l) => years.find((y) => y.value === l.academicYearId)?.label ?? "—", filter: "Any year" },
  { key: "kind", label: "For", accessor: (l) => KIND_LABELS[l.kind] ?? l.kind },
  { key: "students", label: "Students", accessor: (l) => l.students, sortValue: (l) => l.students },
  { key: "total", label: "Total", accessor: (l) => formatRwf(l.totalTuition + l.totalAllowance), sortValue: (l) => l.totalTuition + l.totalAllowance },
  { key: "state", label: "Status", accessor: (l) => stateOf(l.state).label, render: (l) => <ListStateTag state={l.state} /> },
  { key: "updated", label: "Last change", accessor: (l) => formatDateTime(l.updatedAt), sortValue: (l) => l.updatedAt },
];

const STUDENT_COLUMNS = [
  { key: "regNumber", label: "Reg. number", accessor: (e) => e.regNumber },
  { key: "name", label: "Name", accessor: nameOf, className: "font-medium text-slate-900" },
  { key: "nationalId", label: "National ID", accessor: (e) => e.nationalId },
  { key: "programme", label: "Programme", accessor: (e) => e.programme },
  { key: "year", label: "Year", accessor: (e) => e.yearOfStudy, sortValue: (e) => e.yearOfStudy },
  { key: "tuition", label: "Tuition", accessor: (e) => formatRwf(e.tuitionFee), sortValue: (e) => e.tuitionFee },
  { key: "allowance", label: "Allowance", accessor: (e) => formatRwf(e.allowance), sortValue: (e) => e.allowance },
];

// The students on a list; on a draft each row can be edited or removed
export function StudentsTable({ entries, exportName, onEdit, onRemove, emptyTitle = "No students on this list" }) {
  const columns = onEdit
    ? [
        ...STUDENT_COLUMNS,
        {
          key: "actions",
          label: "",
          exportable: false,
          render: (e) => (
            <div className="flex justify-end gap-1">
              <RowAction Icon={PencilSquareIcon} label={`Edit ${nameOf(e)}`} onClick={() => onEdit(e)} />
              <RowAction Icon={TrashIcon} label={`Remove ${nameOf(e)}`} onClick={() => onRemove(e)} />
            </div>
          ),
        },
      ]
    : STUDENT_COLUMNS;
  return <Table columns={columns} data={entries} rowKey={(e) => e.regNumber} searchPlaceholder="Search by name or reg. number" exportName={exportName} emptyTitle={emptyTitle} />;
}

// Where the list has been, newest first, with the reason of a rejection
export function ListHistory({ history }) {
  return (
    <Card title="History">
      <ol className="flex flex-col gap-3">
        {[...history].reverse().map((h, i) => (
          <li key={`${h.step}-${h.at}-${i}`} className="flex flex-col gap-0.5 border-l-2 border-slate-200 pl-3">
            <span className="text-sm font-medium text-slate-800">{stepLabel(h.step)}</span>
            <span className="text-xs text-slate-500">{formatDateTime(h.at)}</span>
            {h.note && <span className="text-sm text-slate-600">Reason: {h.note}</span>}
          </li>
        ))}
      </ol>
    </Card>
  );
}
